import React, { useState, useEffect } from 'react';
import { warrantyApi } from '../../services/api';

interface WarrantyRegistration {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  customerAddress?: string;
  customerCity?: string;
  customerState?: string;
  customerZip?: string;
  installDate: string;
  installerName?: string;
  installerCompany?: string;
  dealerCompanyName?: string;
  status: string;
  registeredAt: string;
  motorWarrantyExpiresAt: string;
  fabricWarrantyExpiresAt: string;
  frameWarrantyExpiresAt: string;
  photoUrls: string[];
  notes?: string;
}

const statusColors: Record<string, string> = {
  registered: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  expired: 'bg-gray-100 text-gray-800',
  voided: 'bg-red-100 text-red-800',
  pending_review: 'bg-amber-100 text-amber-800',
  rejected: 'bg-red-100 text-red-800',
};

const statusLabels: Record<string, string> = {
  registered: 'Registered',
  active: 'Active',
  expired: 'Expired',
  voided: 'Voided',
  pending_review: 'Pending Review',
  rejected: 'Rejected',
};

export default function WarrantyRegistrationsPage() {
  const [registrations, setRegistrations] = useState<WarrantyRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Selected registration for detail view
  const [selectedRegistration, setSelectedRegistration] = useState<WarrantyRegistration | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  useEffect(() => {
    fetchRegistrations();
  }, [page, searchQuery, statusFilter]);

  const handleApprove = async (id: string) => {
    if (!confirm('Approve this consumer warranty registration? This makes it official and emails the customer their confirmation.')) return;
    setApprovingId(id);
    try {
      await warrantyApi.approveRegistration(id);
      await fetchRegistrations();
    } catch (err: any) {
      alert(err.message || 'Failed to approve');
    } finally {
      setApprovingId(null);
    }
  };

  const fetchRegistrations = async () => {
    setLoading(true);
    setError(null);

    try {
      const params: any = { page, pageSize: 20 };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await warrantyApi.listRegistrations(params);
      setRegistrations(response.data || []);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Warranty Registrations</h1>
        <p className="text-gray-500">View all registered warranties across dealers</p>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by order number, customer name, dealer..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="pending_review">Pending Review</option>
            <option value="registered">Registered</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="voided">Voided</option>
          </select>
        </div>
      </div>

      {/* Results count */}
      {!loading && (
        <div className="text-sm text-gray-500">
          Showing {registrations.length} of {total} registrations
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Loading registrations...</p>
        </div>
      ) : registrations.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">No warranty registrations found</p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Dealer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Install Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Registered
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {registrations.map((reg) => (
                    <tr key={reg.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-blue-600 font-medium">{reg.orderNumber || '-'}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="font-medium text-gray-900">{reg.customerName}</p>
                          <p className="text-gray-500 text-sm">{reg.customerEmail}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {reg.dealerCompanyName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {formatDate(reg.installDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[reg.status]}`}
                        >
                          {statusLabels[reg.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-sm">
                        {formatDate(reg.registeredAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setSelectedRegistration(reg)}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View Details
                          </button>
                          {reg.status === 'pending_review' && (
                            <button
                              onClick={() => handleApprove(reg.id)}
                              disabled={approvingId === reg.id}
                              className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {approvingId === reg.id ? '…' : 'Approve'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 disabled:opacity-50 hover:bg-gray-100"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 disabled:opacity-50 hover:bg-gray-100"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      {selectedRegistration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-bold text-gray-900">Warranty Registration Details</h2>
                <button
                  onClick={() => setSelectedRegistration(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Order & Dealer Info */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Order Information</h3>
                    <div className="space-y-2">
                      <div>
                        <span className="text-gray-500 text-sm">Order Number:</span>
                        <p className="font-medium">{selectedRegistration.orderNumber || '-'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-sm">Install Date:</span>
                        <p className="font-medium">{formatDate(selectedRegistration.installDate)}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Dealer Information</h3>
                    <div className="space-y-2">
                      <div>
                        <span className="text-gray-500 text-sm">Company:</span>
                        <p className="font-medium">{selectedRegistration.dealerCompanyName || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer Info */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Customer Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-500 text-sm">Name:</span>
                      <p className="font-medium">{selectedRegistration.customerName}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 text-sm">Email:</span>
                      <p className="font-medium">{selectedRegistration.customerEmail}</p>
                    </div>
                    {selectedRegistration.customerPhone && (
                      <div>
                        <span className="text-gray-500 text-sm">Phone:</span>
                        <p className="font-medium">{selectedRegistration.customerPhone}</p>
                      </div>
                    )}
                    {selectedRegistration.customerAddress && (
                      <div className="col-span-2">
                        <span className="text-gray-500 text-sm">Address:</span>
                        <p className="font-medium">
                          {selectedRegistration.customerAddress}
                          {selectedRegistration.customerCity && `, ${selectedRegistration.customerCity}`}
                          {selectedRegistration.customerState && `, ${selectedRegistration.customerState}`}
                          {selectedRegistration.customerZip && ` ${selectedRegistration.customerZip}`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Warranty Coverage */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Warranty Coverage</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-gray-500 text-sm">Motor</p>
                        <p className="font-medium">5 Years</p>
                        <p className="text-gray-500 text-xs">
                          Expires: {formatDate(selectedRegistration.motorWarrantyExpiresAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Fabric</p>
                        <p className="font-medium">10 Years</p>
                        <p className="text-gray-500 text-xs">
                          Expires: {formatDate(selectedRegistration.fabricWarrantyExpiresAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Frame</p>
                        <p className="font-medium">Lifetime</p>
                        <p className="text-gray-500 text-xs">N/A</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Installation Photos */}
                {selectedRegistration.photoUrls && selectedRegistration.photoUrls.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Installation Photos</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {selectedRegistration.photoUrls.map((url, index) => (
                        <a key={index} href={url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={url}
                            alt={`Installation ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg hover:opacity-80 transition-opacity"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Installer Info */}
                {(selectedRegistration.installerName || selectedRegistration.installerCompany) && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Installer Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedRegistration.installerName && (
                        <div>
                          <span className="text-gray-500 text-sm">Installer:</span>
                          <p className="font-medium">{selectedRegistration.installerName}</p>
                        </div>
                      )}
                      {selectedRegistration.installerCompany && (
                        <div>
                          <span className="text-gray-500 text-sm">Company:</span>
                          <p className="font-medium">{selectedRegistration.installerCompany}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedRegistration.notes && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                      {selectedRegistration.notes}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedRegistration(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
